// DS: PO20
import React from 'react';
import { compose } from 'redux';
import { WithTranslation, withTranslation } from 'react-i18next';
import { PapiDeviation, PapiGallection, PapiUser } from '@wix/da-papi-types';
import Modal, {
  MODAL_FOOTER_BUTTON_SIZE,
  MODAL_FOOTER_PRIMARY_BUTTON_MIN_WIDTH,
} from '@wix/da-ds/pkg/modals/dsPresets/Modal';
import BrandedButton from '@wix/da-ds/pkg/Buttons/BrandedButton';
import { BiLoggerContextProvider } from '@wix/da-bi/pkg/BiLogger.context';
import Sidebar from '../_partials/Sidebar';
import { MediaSourceType, MediaPopupMode } from '../types';
import {
  DEFAULT_MEDIASOURCE_TYPES_FOR_MEDIA_MANAGER,
  MAX_SELECTED_ITEMS,
} from '../constants';
import MediaSourceMenu from '../_partials/MediaSourceMenu';
import GallectionManager from '../Gallections/GallectionManager';
import GallectionContentManager from '../Gallections/GallectionContentManager';
import CustomSetDeviationManager from '../Deviations/DeviationManager/CustomSetDeviationManager';
import withOwnReduxStore from '../_hocs/withOwnReduxStore';
import withCurrentMediaSource, {
  WithCurrentMediaSourceProps,
} from '../_hocs/withCurrentMediaSource';
import withCurrentGallection, {
  WithCurrentGallectionProps,
} from '../_hocs/withCurrentGallection';
import withMediaPopupInit, {
  WithMediaPopupInitProps,
} from '../_hocs/withMediaPopupInit';
import withCustomSetDeviations, {
  WithCustomSetDeviationsProps,
} from '../_hocs/withCustomSetDeviations';
import { DeviationTypes } from '../../types/deviation';
import {
  withMobileContext,
  WithMobileContextProps,
} from '@wix/da-react-context/pkg/MobileContext';
import withCurrentTier, {
  WithCurrentTierProps,
} from '../_hocs/withCurrentTier';
import ModalsForMM from '../_partials/ModalsForMM';
import NotificationsForMM from '../_partials/NotificationsForMM/NotificationsForMM';
import TierDeviationManager from '../Deviations/DeviationManager/TierDeviationManager';
import TierExplorer from '../Tiers/TierExplorer';
import DndProviderWrapper from '../../Dnd/DndProviderWrapper';

import mediaPopupStyles from '../MediaPopup.scss';

export interface Props {
  title?: string;
  initialMediaSource?: MediaSourceType;
  withMediaSourceSelector?: boolean;
  mediaSourceTypes?: MediaSourceType[];
  initialDeviationSet?: PapiDeviation[];
  gallectionFolder?: PapiGallection;
  tier?: PapiDeviation;
  allowedDeviationTypes?: DeviationTypes[];
  withNewGallectionPopupOpen?: boolean;
  withAddDeviationsPopupOpen?: boolean;
  onDone: (deviations?: PapiDeviation[]) => void;
  onClose: (event?: Event) => void;
  allowUploads?: boolean;
  gruser?: PapiUser;
}

const enhance = compose(
  withOwnReduxStore,
  withCurrentMediaSource,
  withMediaPopupInit,
  withCurrentGallection,
  withCurrentTier,
  withCustomSetDeviations, // for onDone event
  withMobileContext,
  withTranslation()
);
class MediaManager extends React.Component<
  Props &
    WithCurrentMediaSourceProps &
    WithCurrentGallectionProps &
    WithCurrentTierProps &
    WithCustomSetDeviationsProps &
    WithMediaPopupInitProps &
    WithMobileContextProps &
    WithTranslation
> {
  static defaultProps = {
    initialMediaSource: MediaSourceType.GALLERY,
    withMediaSourceSelector: false,
    mediaSourceTypes: DEFAULT_MEDIASOURCE_TYPES_FOR_MEDIA_MANAGER,
    withNewGallectionPopupOpen: false,
    withAddDeviationsPopupOpen: false,
  };
  componentDidMount() {
    const {
      initialMediaSource,
      initialDeviationSet,
      initMediaPopup,
      gallectionFolder,
      tier,
      allowedDeviationTypes,
      mediaSourceTypes,
      withMediaSourceSelector,
      withNewGallectionPopupOpen = false,
      withAddDeviationsPopupOpen = false,
      allowUploads = true,
      gruser,
    } = this.props;
    const selectionLimit = MAX_SELECTED_ITEMS;
    let mediaSource = initialMediaSource!;
    if (initialDeviationSet) {
      mediaSource = MediaSourceType.CUSTOM_SET;
    }
    initMediaPopup({
      mode: MediaPopupMode.MANAGER,
      mediaSource,
      initialDeviationSet,
      selectionLimit,
      gallectionFolder,
      tier,
      allowedDeviationTypes,
      mediaSourceTypes,
      withMediaSourceSelector,
      withNewGallectionPopupOpen: withNewGallectionPopupOpen,
      withAddDeviationsPopupOpen,
      allowUploads,
      gruser,
    });
  }

  render() {
    const { onClose, withMediaSourceSelector, t, isMobile } = this.props;
    if (isMobile) {
      // MediaManager is not supported on mobile
      // only MediaSelector
      return null;
    }

    return (
      <DndProviderWrapper>
        <BiLoggerContextProvider value={{ sectionname: 'media-manager' }}>
          <Modal
            isOpen
            className={mediaPopupStyles['root']}
            contentClassName={mediaPopupStyles['content']}
            heading={this.getTitle()}
            showHeaderBorder
            showFooterBorder
            onRequestClose={() => onClose()}
            footerRight={[
              <BrandedButton
                key="done"
                variant="brand-green"
                size={MODAL_FOOTER_BUTTON_SIZE}
                style={{ minWidth: MODAL_FOOTER_PRIMARY_BUTTON_MIN_WIDTH }}
                onClick={this.onDone}
              >
                {t('mediapopup.done')}
              </BrandedButton>,
            ]}
          >
            {withMediaSourceSelector && (
              <Sidebar>
                <MediaSourceMenu
                  mediaSourceTypes={[
                    MediaSourceType.GALLERY,
                    MediaSourceType.COLLECTION,
                  ]}
                />
              </Sidebar>
            )}
            {this.renderContentArea()}
            <NotificationsForMM />
          </Modal>
          <ModalsForMM />
        </BiLoggerContextProvider>
      </DndProviderWrapper>
    );
  }

  renderContentArea() {
    const {
      initialDeviationSet,
      currentMediaSource,
      currentGallection,
      currentTier,
    } = this.props;
    if (initialDeviationSet) {
      return <CustomSetDeviationManager />;
    }
    if (
      currentMediaSource &&
      [MediaSourceType.TIER_DEVIATIONS, MediaSourceType.TIER_POSTS].includes(
        currentMediaSource
      )
    ) {
      if (currentTier) {
        return <TierDeviationManager />;
      }
      return <TierExplorer />;
    }
    if (!currentGallection) {
      return <GallectionManager />;
    }
    return <GallectionContentManager />;
  }

  getTitle() {
    const { currentMediaSource, t, title } = this.props;

    if (title) {
      return title;
    }

    switch (currentMediaSource) {
      case MediaSourceType.GALLERY:
        return t!('mediapopup.editYourGalleries');
      case MediaSourceType.COLLECTION:
        return t!('mediapopup.editYourCollections');
      case MediaSourceType.PRIVATE_COLLECTION:
        return t!('mediapopup.editYourPrivateCollections');
      case MediaSourceType.CUSTOM_SET:
        return t!('mediapopup.editYourGallery');
      case MediaSourceType.STASH:
        return t('mediapopup.editYourStash');
      default:
        return t!('mediapopup.title');
    }
  }

  onDone = () => {
    const { deviations, currentMediaSource } = this.props;

    if (currentMediaSource === MediaSourceType.CUSTOM_SET) {
      this.props.onDone(deviations);
    } else {
      this.props.onDone();
    }
  };
}

export default enhance(MediaManager) as React.ComponentType<Props>;
